import type { FetchApi, BasicFetch, RequestInit, Response } from '@mimir/common/types/basicFetch';
import { invariant } from '@mimir/common/util/invariant';
import Axios from 'axios';
import type { AxiosResponse, AxiosStatic, Method } from 'axios';
import { isAxiosError } from '@mimir/common/util/httpError';
import { ABORT_ERROR } from '@mimir/common/util/abortControl';
import { makeError } from '@mimir/common/util/exception';

export function makeBrowserFetch(windowGlobal: typeof window): FetchApi {
  invariant(windowGlobal, 'makeBrowserFetch expected windowGlobal');
  return {
    fetch: windowGlobal.fetch.bind(windowGlobal) as unknown as BasicFetch,
    AbortController: windowGlobal.AbortController as new () => AbortController
  };
}

/**
 * Only used as 'fetch' in share page for upload so that we can show more fine grained
 * upload progress which is not supported by the fetch api.
 */
export function makeBrowserFetchFromAxios(axios: AxiosStatic): FetchApi {
  const axiosInstance = axios.create();
  const browserFetch = async (url: string, init: RequestInit): Promise<Response> => {

    let axiosHeaders: Record<string, string> | undefined;
    if (init.headers != null) {
      axiosHeaders = {};
      for (const [k, v] of init.headers) {
        let values = axiosHeaders[k];
        if (values === undefined) {
          values = v;
        } else {
          values += ', ' + v;
        }
        axiosHeaders[k] = values;
      }
    }

    let resp: AxiosResponse<Blob> | undefined;
    try {
      resp = await axiosInstance.request<Blob>({
        url,
        headers: axiosHeaders,
        responseType: 'blob',
        signal: init.signal,
        method: init.method as Method ?? 'GET',
        data: init.body,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: init.progressFuncs?.onUploadProgress
      });
    } catch(ex) {
      if (Axios.isCancel(ex)) {
        throw makeError('Aborted', ABORT_ERROR, { cause: ex });
      }
      // we need to reset upload progress in case of failure
      if (init.progressFuncs) {
        init.progressFuncs.onUploadFail();
      }
      if (isAxiosError(ex) && ex.response && ex.response.status >= 400) {
        // The response data is a Blob because we requested responseType: 'blob'
        invariant(
          ex.response.data == null || ex.response.data instanceof Blob,
          'Expected response data to be a Blob or null for blob responseType'
        );
        resp = ex.response as AxiosResponse<Blob>;
      } else {
        throw makeError('NetworkError when attempting to fetch resource', 'TypeError', { cause: ex });
      }
    }

    invariant(resp, 'Response must be defined after successful request or error handling');
    const response = resp;
    const body = response.data?.stream() ?? null;
    return {
      ok: response.status > 0 && response.status < 400,
      status: response.status,
      statusText: response.statusText,
      headers: {
        get(name: string): string | null {
          return response.headers[name.toLowerCase()] ?? null;
        }
      },
      body,
      text: () => response.data.text(),
      async json(): Promise<any> {
        const json = JSON.parse(await response.data.text());
        return json;
      },
    };
  };
  return {
    fetch: browserFetch,
    AbortController: AbortController,
  };
}
